import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { BaseComponent } from '../../../base/base.component';
import { AddressLabelComponent } from '../../../core/labels/address-label.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { CasePipe } from '../../../../pipes/case.pipe';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import { ContactResponse } from '../../../../models/api/view/claim/details/contacts/contact-response';
import { addKey } from '../../../../functions/add-key';
import { isNullOrWhiteSpace } from '../../../../functions/is-null-or-white-space';
import { DateEx } from '../../../../functions/date-ex';

@Component({
  selector: 'app-address-book',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    TableComponent,
    ColumnComponent,
    RouterModule,
    PageHeaderComponent,
    AddressLabelComponent,
    CasePipe,
  ],
  template: `
    <app-page-header label="Contacts" />
    <app-table
      [dataSource]="this.contactData?.contacts"
      [pageSize]="10"
      [scrollable]="false"
      [expandable]="true"
      dataKey="_key"
      [canExpand]="canExpand"
      recordLabel="Contact"
      [globalFilterFields]="[
        'contact.name',
        'contact.typeAttribute',
        'contact.phone1',
        'contact.fax',
        'contact.email',
        'contact.address',
        'contact.city',
        'contact.state',
        'contact.zip',
        'contact.country',
      ]"
    >
      <app-column name="fullName" label="Name" sortName="contact.name">
        <ng-template let-data>
          <b>{{ data.contact.name | case: 'titlecase' }}</b>
        </ng-template>
      </app-column>
      <app-column name="contact.typeAttribute" label="Type" />
      <app-column name="contact.phone1" label="Phone" format="p" />
      <app-column name="contact.fax" label="Fax" format="p" />
      <app-column name="contact.email" label="Email" />
      <app-column name="contact.address" label="Address">
        <ng-template let-data #value>
          <app-address-label
            [street1]="data.contact.address1 ?? data.contact.address"
            [street2]="data.contact.address2"
            [city]="data.contact.city"
            [state]="data.contact.state"
            [postalCode]="data.contact.zip"
            [leftAlign]="true"
          />
        </ng-template>
      </app-column>

      <ng-template #expand let-data>
        <div class="tw-flex tw-gap-8">
          <div class="tw-basis-1/4">
            <app-detail-panel
              [dataSource]="data.details"
              [showIcons]="false"
              [showEmpty]="false"
            >
              @if (data.details.phone2) {
                <app-detail-item label="Other phone" name="phone2" format="p" />
              }
              @if (data.details.authorized) {
                <app-detail-item
                  label="Authorized"
                  name="authorized"
                  format="b"
                />
              }
              @if (data.details.authBegin) {
                <app-detail-item
                  label="Authorized Begin"
                  name="authBegin"
                  format="d"
                />
              }
              @if (data.details.authEnd) {
                <app-detail-item
                  label="Authorized End"
                  name="authEnd"
                  format="d"
                />
              }
              @if (data.details.comment) {
                <app-detail-item label="Comment" name="comment" />
              }
            </app-detail-panel>
          </div>
          <div class="tw-basis-1/4">
            <app-detail-panel
              [dataSource]="data.details"
              [showIcons]="true"
              [showEmpty]="false"
            >
              @if (data.details.cceWage > 0) {
                <app-detail-item
                  label="CCE wage"
                  name="cceWage"
                  labelCase="none"
                  format="c"
                  [zeroEmpty]="true"
                />
              }
              @if (data.details.contactName) {
                <app-detail-item label="Contact" name="contactName" />
              }
              @if (data.details.date) {
                <app-detail-item label="Contact Date" name="date" />
              }
              @if (data.details.place) {
                <app-detail-item label="Contact Place" name="place" />
              }
            </app-detail-panel>
          </div>
        </div>
      </ng-template>
    </app-table>
  `,
  styles: ``,
})
export class AddressBookComponent extends BaseComponent implements OnInit {
  currentClaim: ClaimRow;
  contactData: ContactResponse;

  canExpand = (row: any): boolean => {
    if (!row?.details || typeof row.details !== 'object') {
      return false;
    }
    return this.hasContactDetails(row.details);
  };

  private hasContactDetails(details: any): boolean {
    const hasPhone2 = !isNullOrWhiteSpace(details.phone2);
    const isAuthorized = details.authorized === true;
    const hasAuthBegin = !isNullOrWhiteSpace(details.authBegin);
    const hasAuthEnd = !isNullOrWhiteSpace(details.authEnd);
    const hasComment = !isNullOrWhiteSpace(details.comment);
    const hasCceWage =
      details.cceWage !== undefined &&
      details.cceWage !== null &&
      details.cceWage > 0;
    const hasContactName = !isNullOrWhiteSpace(details.contactName);
    const hasDate = !isNullOrWhiteSpace(details.date);
    const hasPlace = !isNullOrWhiteSpace(details.place);

    return (
      hasPhone2 ||
      isAuthorized ||
      hasAuthBegin ||
      hasAuthEnd ||
      hasComment ||
      hasCceWage ||
      hasContactName ||
      hasDate ||
      hasPlace
    );
  }

  async ngOnInit(): Promise<void> {
    this.view.showLoading();
    this.currentClaim = this.view.currentClaim();

    const claimKey: ClaimKey = {
      recordId: this.currentClaim.recordId,
      recordSource: this.currentClaim.recordSource,
    };

    this.contactData = await this.api.contacts.get(claimKey);

    if (!this.contactData.contacts) {
      this.contactData.contacts = [];
    }

    if (this.contactData?.contacts) {
      this.contactData.contacts.forEach((item) => {
        (item as any).contactId = item.contact?.id;
        if (isNullOrWhiteSpace(item.contact?.name)) {
          item.contact.name = [item.contact.firstName, item.contact.lastName]
            .filter((x) => !isNullOrWhiteSpace(x))
            .join(' ');
        }
        const formatted = DateEx.formatDateWithTime(
          item?.details?.date as any,
          item?.contact?.time as any,
        );
        if (!isNullOrWhiteSpace(formatted)) {
          (item.details as any).date = formatted;
        }
      });
      this.contactData.contacts = addKey(this.contactData.contacts);
    }
    this.view.hideLoading();
  }
}
